/**
 * useAnonymizedJob — Sprint 2 / TASK 4-2
 *
 * Returns a version of the given job with company-identifying fields masked
 * when blind-mode is active.  The mapping is stable for the current browser
 * session: the first time a job_id is seen it is assigned the next letter
 * ("Company A", "Company B", …) and that mapping is persisted to
 * `sessionStorage` so navigating away and back keeps labels consistent.
 *
 * Rules:
 *  • When blind mode is OFF, the original job object is returned as-is.
 *  • When blind mode is ON:
 *      - `company`           → "Company A/B/C…"
 *      - `company_logo_url`  → undefined   (CompanyLogo falls back to initial)
 *  • Underlying `id`, `url`, `external_job_id` are NEVER touched, so Apply
 *    CTA links still work correctly.
 */

import type { Job } from '../components/JobCard';
import { useBlindModeValue } from './useBlindMode';

// ── Session-stable label map ──────────────────────────────────────────────────

const SS_KEY = 'ggj:blind-company-map';

function loadMap(): Map<string, string> {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return new Map();
    const entries = JSON.parse(raw) as [string, string][];
    return new Map(entries);
  } catch {
    return new Map();
  }
}

function saveMap(map: Map<string, string>): void {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify([...map.entries()]));
  } catch {
    // sessionStorage full or unavailable — labels just won't persist across
    // navigations, which is an acceptable degradation.
  }
}

// Alphabet sequence: A, B, …, Z, AA, AB, …
function nextLabel(index: number): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (index < 26) return `Company ${alphabet[index]}`;
  const outer = Math.floor(index / 26) - 1;
  const inner = index % 26;
  return `Company ${alphabet[outer]}${alphabet[inner]}`;
}

// Single in-memory map (also backed by sessionStorage for persistence).
const inMemory: Map<string, string> = loadMap();

function getOrCreateLabel(jobId: string): string {
  if (inMemory.has(jobId)) return inMemory.get(jobId)!;
  const label = nextLabel(inMemory.size);
  inMemory.set(jobId, label);
  saveMap(inMemory);
  return label;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * When blind mode is active, replaces `company` and `company_logo_url` with
 * stable anonymous equivalents.  All other fields — including `id`, `url`, and
 * `external_job_id` — are left untouched so Apply CTAs keep working.
 */
export function useAnonymizedJob(job: Job): Job {
  const blindMode = useBlindModeValue();

  if (!blindMode) return job;

  const label = getOrCreateLabel(job.id);
  return {
    ...job,
    company: label,
    company_logo_url: undefined,
  };
}
